import type { ComponentPropsWithRef } from "react";
import { cn } from "../../lib/utils.ts";

export interface SwitchProps extends Pick<ComponentPropsWithRef<"button">,
  "id" | "aria-describedby" | "className" | "ref"> {
  /** Keep the label stable as the value changes. */
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  pending?: boolean;
}

/** Controlled binary setting. Native button activation supports Space and Enter. */
export function Switch({ label, checked, onCheckedChange, disabled = false, pending = false, className, ...props }: SwitchProps) {
  return (
    <button
      {...props}
      type="button"
      role="switch"
      aria-label={label}
      aria-checked={checked}
      aria-busy={pending || undefined}
      disabled={disabled || pending}
      onClick={() => { if (!disabled && !pending) onCheckedChange(!checked); }}
      className={cn(
        "inline-flex min-h-12 min-w-12 shrink-0 items-center justify-center rounded-lg",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
    >
      <span aria-hidden="true" className={cn("inline-flex h-6 w-10 items-center rounded-lg p-1 transition-colors motion-reduce:transition-none", checked ? "bg-brand-600" : "bg-slate-500")}>
        <span className={cn("h-4 w-4 rounded-sm bg-white transition-transform motion-reduce:transition-none", checked && "translate-x-4")} />
      </span>
    </button>
  );
}
